/**
 * Area route registration — composition shell wires URL params/search to
 * AreaRoute. The historical "do not edit router.tsx in 5/6/7" freeze is
 * lifted for this fourth screen (plan §3.1 router.tsx child; area-taxonomy
 * risk table). AD-9 is the tiny-area / reuse contract.
 */
import { createRoute, useNavigate } from '@tanstack/react-router';
import { lazy, Suspense, useCallback, type JSX } from 'react';
import { z } from 'zod';

import {
  AreaIdSchema,
  AreaSearchParamsSchema,
  type AreaId,
  type AreaSearchParams,
  type Ticker,
} from '@meridian/contracts';

import { Skeleton } from '@/components/primitives/skeleton';
import { stockTarget } from '@/lib/nav/targets';
import { rootRoute } from '@/router-root';

import { AreaRouteError } from './area-route-error';
import { InvalidAreaParamError } from './invalid-area-param-error';

const loadAreaRoute = () => import('./area-route');

const AreaScreen = lazy(async () => {
  const module = await loadAreaRoute();
  return { default: module.AreaRoute };
});

const paramsSchema = z.object({
  areaId: AreaIdSchema,
});

/** Zod-at-the-boundary parse. Throws the sentinel, never a raw ZodError. */
export function parseAreaRouteParams(raw: Record<string, unknown>): { readonly areaId: AreaId } {
  const result = paramsSchema.safeParse(raw);
  if (!result.success) {
    throw new InvalidAreaParamError();
  }
  return result.data;
}

/** Stock hop — the area screen is industry-only, so tax is pinned, not copied. */
export function stockDetailTarget(
  ticker: Ticker,
  search: Pick<AreaSearchParams, 'win' | 'idx' | 'book' | 'cmp'> & { readonly area: AreaId },
): ReturnType<typeof stockTarget> {
  return stockTarget(ticker, { ...search, tax: 'industry' });
}

function AreaRouteAdapter(): JSX.Element {
  const { areaId } = areaRoute.useParams();
  const search = areaRoute.useSearch();
  const navigate = useNavigate();

  const onTickerSelect = useCallback(
    (ticker: Ticker) => {
      void navigate(
        stockDetailTarget(ticker, {
          win: search.win,
          idx: search.idx,
          book: search.book,
          cmp: search.cmp,
          area: areaId,
        }),
      );
    },
    [navigate, search.win, search.idx, search.book, search.cmp, areaId],
  );

  return (
    <Suspense fallback={<Skeleton height="18rem" label="Loading area" />}>
      <AreaScreen
        areaId={areaId}
        window={search.win}
        index={search.idx}
        book={search.book}
        compare={search.cmp}
        onTickerSelect={onTickerSelect}
      />
    </Suspense>
  );
}

export const areaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/area/$areaId',
  params: {
    parse: (raw) => parseAreaRouteParams(raw),
    stringify: (p) => ({ areaId: p.areaId }),
  },
  validateSearch: (search: Record<string, unknown>) => AreaSearchParamsSchema.parse(search),
  loader: loadAreaRoute,
  component: AreaRouteAdapter,
  errorComponent: AreaRouteError,
});
